import type { EntrySession, ModelAnswer } from './types.ts';

/**
 * SMART ENTRY (docs/SMART-ENTRY.md): the player can type the whole word *or* just the
 * gaps, and Arrow infers which from what they type. Never from the solution: only the
 * visible entries and the locked (solved, so known-right) cells.
 *
 * An entry session remembers where typing started, the answer's entries at that moment
 * and the letters typed since. Every keystroke re-places the whole buffer from that
 * snapshot, so a later letter can correct an earlier guess about intent.
 */

/**
 * Where the cursor goes after typing at `index` (gap mode, the original rule):
 * 1. the next empty, editable cell after it;
 * 2. else (wrap) the first empty, editable cell before it;
 * 3. else — the answer is full — the next editable cell after it (so a run of
 *    corrections can be overtyped), staying put at the end of the answer.
 * `values` and `locked` are by index within the answer.
 */
export function advanceIndex(index: number, values: readonly string[], locked: readonly boolean[]): number {
	const n = values.length;
	const editable = (i: number) => !locked[i];
	const empty = (i: number) => editable(i) && values[i] === '';
	for (let i = index + 1; i < n; i++) if (empty(i)) return i;
	for (let i = 0; i < index; i++) if (empty(i)) return i;
	for (let i = index + 1; i < n; i++) if (editable(i)) return i;
	return index;
}

/**
 * How long (ms) after an answer solves mid-word its remaining letters are still read as
 * the player finishing that word (see `GameState.echo`). A whole-word typist types them
 * within a few hundred ms; a gap typist reads the next clue first.
 */
export const ECHO_WINDOW_MS = 1000;

/** A fresh session at the cursor, before any letter is typed. */
export function startSession(
	answer: ModelAnswer,
	index: number,
	entries: readonly string[],
	locked: ReadonlySet<number>,
): EntrySession {
	return {
		answerId: answer.id,
		start: index,
		snapshot: answer.cells.map((c) => entries[c] ?? ''),
		locked: answer.cells.map((c) => locked.has(c)),
		buffer: '',
		at: index,
		mode: 'gap',
	};
}

export interface Placement {
	/** The answer's letters after placing the buffer, by index (locked cells unchanged). */
	values: string[];
	/** Where the cursor goes. */
	cursor: number;
	mode: 'gap' | 'through';
	/** Type-through start (`t`), when `mode` is `'through'`. */
	from?: number;
	/** Index the newest letter landed on (undefined when it was ignored past the end). */
	landed?: number;
	/**
	 * Set when the buffer also reads as the whole word typed from its start: the index
	 * just after where that reading ends. If the answer solves before the end, the
	 * letters from here on are what the player is still about to type (`echo`).
	 */
	wordEnd?: number;
}

/**
 * Places `buffer` from the session's snapshot. `current` / `lockedNow` are the answer's
 * entries and locks right now (a crossing solved mid-session can lock one of its cells;
 * a locked cell is never changed).
 */
export function placeBuffer(
	session: EntrySession,
	buffer: string,
	current: readonly string[],
	lockedNow: readonly boolean[],
): Placement {
	const { snapshot, start: s } = session;
	const n = snapshot.length;
	const isLocked = (i: number) => session.locked[i] === true || lockedNow[i] === true;
	const lockedLetter = (i: number) => (lockedNow[i] ? current[i]! : snapshot[i]!);

	/** `strict`: unlocked filled cells must match too (a pure whole-word reading). */
	const through = (t: number, strict = false): Placement | null => {
		const values = [...snapshot];
		let landed: number | undefined;
		for (let k = 0; k < buffer.length; k++) {
			const i = t + k;
			if (i >= n) {
				// Past the end: like gap mode, the last cell is overtyped (if it's editable).
				landed = isLocked(n - 1) ? undefined : n - 1;
				if (landed !== undefined) values[n - 1] = buffer[k]!;
				continue;
			}
			landed = i;
			if (isLocked(i)) {
				// A locked cell must match: it's a confirmation, nothing is written.
				if (lockedLetter(i) !== buffer[k]) return null;
				values[i] = lockedLetter(i);
			} else if (strict && snapshot[i] !== '' && snapshot[i] !== buffer[k]) return null;
			else values[i] = buffer[k]!;
		}
		return { values, cursor: Math.min(t + buffer.length, n - 1), mode: 'through', from: t, landed, wordEnd: t + buffer.length };
	};

	const gap = (): Placement => {
		const values = [...snapshot];
		let pos = s;
		let landed: number | undefined;
		for (const letter of buffer) {
			landed = pos;
			if (!session.locked[pos]) values[pos] = letter;
			pos = advanceIndex(pos, values, session.locked);
		}
		// Never change a cell that has locked since the session started.
		for (let i = 0; i < n; i++) if (lockedNow[i]) values[i] = current[i]!;
		// Gap mode from cell 0 that has written 0, 1, 2… in a row is also a whole word.
		const contiguous = s === 0 && landed === buffer.length - 1;
		return { values, cursor: pos, mode: 'gap', landed, ...(contiguous ? { wordEnd: buffer.length } : {}) };
	};

	// 1. Whole word from the start (the spec's rule): `s > 0`, everything before the
	//    cursor is filled and the first letter equals cell 0 (DAFFODIL). The whole-word
	//    reading is speculative. A gap typist whose gaps start with the letters already
	//    showing (DODO as `D O _ _`, DADDY as `D A _ _ Y`, BARBARA) types exactly what a
	//    whole-word typist does, so the gap reading keeps every letter visible until the
	//    buffer shows it's the whole word. A gap typist's letter never vanishes and a
	//    letter showing is never overtyped on a guess:
	//    - more letters than gaps: only the whole word fits (and an unlocked letter it
	//      disagrees with is the player's correction);
	//    - the whole word contradicts a letter showing: the gaps;
	//    - no longer than the letters before the cursor: the whole word would only confirm
	//      them and write nothing, so the gaps (this includes the spec's `D U _` + D);
	//    - past them and short of filling the gaps: the whole word (a whole-word typist
	//      sees one hop, on their second letter);
	//    - exactly filling the gaps: the tie goes to the gaps, as in 1b (a gap typist is
	//      done; a whole-word typist's next letter flips it back), unless the whole word
	//      has already matched a letter showing past the cursor (DAFFODIL's F), which a
	//      gap typist's letters would only do by coincidence.
	//    A gap placement that also reads as the whole word keeps `wordEnd`, so a word that
	//    solves on its gaps still swallows a whole-word typist's remaining letters.
	const gapsFromS = snapshot.slice(s).filter((v, k) => v === '' && !session.locked[s + k]).length;
	if (s > 0 && snapshot.slice(0, s).every((v) => v !== '') && buffer[0] === snapshot[0]) {
		const len = buffer.length;
		if (len > gapsFromS) return through(0) ?? gap();
		const whole = through(0, true);
		if (!whole) return gap();
		let matchedAhead = false;
		for (let i = s; i < Math.min(len, n); i++) if (snapshot[i] !== '' || lockedNow[i]) matchedAhead = true;
		if (len > s && (len < gapsFromS || matchedAhead)) return whole;
		return { ...gap(), wordEnd: len };
	}
	// 1b. Extension (not in the spec): the gap comes first (`s = 0`, e.g. `_ S _ _ _`,
	//     where navigation lands for most crossings). Both readings place the letters
	//     identically up to the first filled cell `f`; from there, type-through while the
	//     buffer matches every filled cell it passes. When the buffer exactly fills the
	//     gaps it's ambiguous (a gap typist is done; a whole-word typist has letters to
	//     go), so the gap reading wins, as in the spec's `D U _` exception: a gap typist
	//     always ends right, and a whole-word typist's next letter flips it back (one
	//     visible hop). A solve on that tie still echoes the rest of the word.
	const f = snapshot.findIndex((v) => v !== '');
	if (s === 0 && f > 0 && buffer.length > f) {
		const whole = through(0, true);
		if (whole) {
			if (buffer.length !== gapsFromS) return whole;
			return { ...gap(), wordEnd: buffer.length };
		}
	}
	// 2. Typing from a filled cell: overtype / confirm from there.
	if (snapshot[s] !== '') return through(s) ?? gap();
	// 3. Gap mode.
	return gap();
}
